import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { updateComponent, selectSelectedComponent } from '../../../redux/editorSlice';
import { extractValueAndUnit } from '../utils/styleUtils';
import { PaddingControl } from '../CommonProperties';

const CompanyHistoryProperties = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();

  // 회사 연혁 아이템 관리 기능
  const handleHistoryItemChange = (index, field, value) => {
    const updatedItems = [...(selectedComponent.content?.historyItems || [])];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        content: {
          ...selectedComponent.content,
          historyItems: updatedItems
        }
      }
    }));
  };
  
  const handleAddHistoryItem = () => {
    const { v4: uuidv4 } = require('uuid');
    const updatedItems = [
      ...(selectedComponent.content?.historyItems || []),
      { 
        id: uuidv4(),
        year: new Date().getFullYear().toString(),
        title: '새 이벤트',
        description: '이벤트 설명을 입력하세요.',
        image: ''
      }
    ];
    
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        content: {
          ...selectedComponent.content,
          historyItems: updatedItems
        }
      }
    }));
  };
  
  const handleRemoveHistoryItem = (index) => {
    const updatedItems = [...(selectedComponent.content?.historyItems || [])];
    updatedItems.splice(index, 1);
    
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        content: {
          ...selectedComponent.content,
          historyItems: updatedItems
        }
      }
    }));
  };
  
  const handleHistoryTitleChange = (value) => {
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        content: {
          ...selectedComponent.content,
          title: value
        }
      }
    }));
  };
  
  const handleHistorySubtitleChange = (value) => {
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        content: {
          ...selectedComponent.content,
          subtitle: value
        }
      }
    }));
  };
  
  const handleTimelineStyleChange = (value) => {
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        content: {
          ...selectedComponent.content,
          timelineStyle: value
        }
      }
    }));
  };
  
  const handleAnimationChange = (value) => {
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        content: {
          ...selectedComponent.content,
          animation: value
        }
      }
    }));
  };
  
  const handleColorSchemeChange = (colorType, value) => {
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        content: {
          ...selectedComponent.content,
          colorScheme: {
            ...(selectedComponent.content?.colorScheme || {
              primary: '#3498db',
              secondary: '#f1c40f',
              background: '#ffffff',
              text: '#333333'
            }),
            [colorType]: value
          }
        }
      }
    }));
  };
  
  const handleImageSizeChange = (value) => {
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        content: {
          ...selectedComponent.content,
          imageSize: value,
          imageWidth: value,
          imageHeight: value
        }
      }
    }));
  };
  
  const handleImageWidthChange = (value) => {
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        content: {
          ...selectedComponent.content,
          imageWidth: value
        }
      }
    }));
  };
  
  const handleImageHeightChange = (value) => {
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        content: {
          ...selectedComponent.content,
          imageHeight: value
        }
      }
    }));
  };

  return (
    <>
      {/* <PaddingControl /> */}
      
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
          </svg>
          회사 연혁 설정
        </div>
        
        {/* 연혁 제목 설정 */}
        <div className="property-control">
          <label>제목</label>
          <input
            type="text"
            value={selectedComponent.content?.title || "회사 연혁"}
            onChange={(e) => handleHistoryTitleChange(e.target.value)}
            className="history-title-input"
          />
        </div>
        
        {/* 연혁 부제목 설정 */}
        <div className="property-control">
          <label>부제목</label>
          <textarea
            value={selectedComponent.content?.subtitle || "저희 회사의 성장 과정을 소개합니다"}
            onChange={(e) => handleHistorySubtitleChange(e.target.value)}
            className="history-subtitle-input"
          />
        </div>
        
        {/* 애니메이션 설정 */}
        <div className="property-row">
          <label>애니메이션 효과</label>
          <div className="custom-toggle-switch">
            <input 
              id="toggle-animation"
              type="checkbox" 
              checked={selectedComponent.content?.animation !== false}
              onChange={(e) => handleAnimationChange(e.target.checked)}
            />
            <label htmlFor="toggle-animation" className="toggle-switch-label">
              <span className="toggle-track"></span>
            </label>
          </div>
        </div>

        {/* 이미지 크기 설정 */}
        <div className="property-control">
          <label>이미지 크기 설정</label>
          <div className="image-size-inputs">
            <div className="image-dimension">
              <label>가로 크기</label>
              <div className="input-with-unit">
                {(() => {
                  const imageWidth = selectedComponent.content?.imageWidth || selectedComponent.content?.imageSize || '150px';
                  const { value, unit } = extractValueAndUnit(imageWidth.toString());
                  return (
                    <>
                      <input 
                        type="number" 
                        value={value}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          if (!isNaN(newValue) && parseFloat(newValue) > 0) {
                            handleImageWidthChange(`${newValue}${unit}`);
                          }
                        }}
                        min="10"
                        step="1"
                      />
                      <select 
                        value={unit}
                        onChange={(e) => {
                          const newUnit = e.target.value;
                          handleImageWidthChange(`${value}${newUnit}`);
                        }}
                      >
                        <option value="px">px</option>
                        <option value="%">%</option>
                        <option value="em">em</option>
                        <option value="rem">rem</option>
                        <option value="vw">vw</option>
                      </select>
                    </>
                  );
                })()}
              </div>
            </div>
            
            <div className="image-dimension">
              <label>세로 크기</label>
              <div className="input-with-unit">
                {(() => {
                  const imageHeight = selectedComponent.content?.imageHeight || selectedComponent.content?.imageSize || '150px';
                  const { value, unit } = extractValueAndUnit(imageHeight.toString());
                  return (
                    <>
                      <input 
                        type="number" 
                        value={value}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          if (!isNaN(newValue) && parseFloat(newValue) > 0) {
                            handleImageHeightChange(`${newValue}${unit}`);
                          }
                        }}
                        min="10"
                        step="1"
                      />
                      <select 
                        value={unit}
                        onChange={(e) => {
                          const newUnit = e.target.value;
                          handleImageHeightChange(`${value}${newUnit}`);
                        }}
                      >
                        <option value="px">px</option>
                        <option value="%">%</option>
                        <option value="em">em</option>
                        <option value="rem">rem</option>
                        <option value="vh">vh</option>
                      </select>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
          
          {/* 미리 정의된 크기 버튼 */}
          <div className="preset-size-buttons">
            <button
              className="preset-size-btn"
              onClick={() => {
                handleImageWidthChange('100px');
                handleImageHeightChange('100px');
              }}
            >
              작게 (100×100)
            </button>
            <button
              className="preset-size-btn"
              onClick={() => {
                handleImageWidthChange('150px');
                handleImageHeightChange('150px');
              }}
            >
              중간 (150×150)
            </button>
            <button
              className="preset-size-btn"
              onClick={() => {
                handleImageWidthChange('200px');
                handleImageHeightChange('200px');
              }}
            >
              크게 (200×200)
            </button>
          </div>
        </div>
        
        {/* 연혁 아이템 관리 */}
        <div className="property-control-section">
          <div className="section-header">
            <label>연혁 항목</label>
            <button 
              className="add-item-btn"
              onClick={handleAddHistoryItem}
            >
              항목 추가
            </button>
          </div>
          
          {(selectedComponent.content?.historyItems || []).map((item, index) => (
            <div key={item.id || index} className="history-item-edit">
              <div className="history-item-header">
                <span>연혁 {index + 1}</span>
                <button 
                  className="remove-history-btn"
                  onClick={() => handleRemoveHistoryItem(index)}
                >
                  삭제
                </button>
              </div>
              
              <div className="property-control">
                <label>연도</label>
                <input
                  type="text"
                  value={item.year}
                  onChange={(e) => handleHistoryItemChange(index, 'year', e.target.value)}
                />
              </div>
              
              <div className="property-control">
                <label>제목</label>
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) => handleHistoryItemChange(index, 'title', e.target.value)}
                />
              </div>
              
              <div className="property-control">
                <label>설명</label>
                <textarea
                  value={item.description}
                  onChange={(e) => handleHistoryItemChange(index, 'description', e.target.value)}
                />
              </div>
              
              <div className="property-control">
                <label>이미지 URL</label>
                <div className="image-url-input-container">
                  <div className="image-url-input-group">
                    <input 
                      type="text" 
                      placeholder="이미지 URL을 입력하세요"
                      value={item.image || ''}
                      onChange={(e) => handleHistoryItemChange(index, 'image', e.target.value)}
                      className="image-url-input"
                    />
                    {item.image && (
                      <button 
                        className="clear-image-btn"
                        onClick={() => handleHistoryItemChange(index, 'image', '')}
                        title="이미지 삭제"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="image-actions-container">
                    <label className="upload-image-btn">
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              formData.append('bucket', 'withcookie-bucket');
                              formData.append('path', 'c/default');
                              
                              const res = await axios.post(
                                `https://refrigerator.logipasta.com/v1/file`,
                                formData,
                                {
                                  headers: {
                                    'Content-Type': 'multipart/form-data',
                                    'Access-Control-Allow-Origin': '*',
                                  },
                                }
                              );
                              
                              if (res.data && res.data.file) {
                                handleHistoryItemChange(index, 'image', res.data.file);
                                alert('이미지가 성공적으로 업로드되었습니다.');
                              }
                            } catch (err) {
                              console.error('[연혁] 이미지 업로드 실패:', err);
                              alert('이미지 업로드에 실패했습니다.');
                            }
                          }
                        }}
                      />
                      <span>이미지 업로드</span>
                    </label>
                  </div>
                  {item.image && (
                    <div className="image-preview">
                      <img src={item.image} alt={`연혁 ${index + 1} 이미지`} />
                      <div className="image-size-badge">
                        {selectedComponent.content?.imageSize || 'medium'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default CompanyHistoryProperties; 